<template>
  <InvoiceTransmissionHeader>
    <div v-if="loading" class="bento-item loader-container">
      <span class="fas fa-lemon"></span>
    </div>

    <!-- Entreprise associée : un seul bento, les informations clés de l'association à la facturation électronique -->
    <div v-else-if="companyInfo" class="bento-item dashboard">
      <div class="hero">
        <div class="hero-main">
          <span class="avatar" aria-hidden="true">{{ initials }}</span>
          <div class="hero-text">
            <h2>{{ companyInfo.formal_name }}</h2>
            <p v-if="companyInfo.trade_name" class="trade-name">{{ companyInfo.trade_name }}</p>
          </div>
        </div>
        <span class="link-status"><i class="dot"></i>Connectée à la facturation électronique</span>
      </div>

      <div class="address-card">
        <span class="eyebrow">Adresse électronique</span>
        <template v-if="companyInfo.electronic_address">
          <div class="address-line">
            <code class="address" :title="companyInfo.electronic_address">{{ companyInfo.electronic_address }}</code>
            <button type="button" class="copy-button" @click="copyAddress(companyInfo.electronic_address)">{{ copied ? 'Copiée' : 'Copier' }}</button>
          </div>
          <p class="address-help">C'est à cette adresse que vos clients et fournisseurs vous envoient leurs factures électroniques.</p>
          <ul v-if="otherAddresses.length" class="address-others">
            <li v-for="address in otherAddresses" :key="address.identifier" :title="address.identifier">
              <code>{{ address.identifier }}</code>
              <span v-if="address.directory" class="chip">{{ address.directory }}</span>
            </li>
          </ul>
        </template>
        <p v-else class="address-empty">Aucune adresse électronique n'est encore enregistrée pour votre entreprise.</p>
      </div>

      <div class="panels">
        <div class="panel">
          <h4>Entreprise</h4>
          <dl>
            <div class="row"><dt>N° entreprise</dt><dd>{{ companyInfo.number }}<span v-if="companyInfo.number_scheme" class="chip">{{ companyInfo.number_scheme }}</span></dd></div>
            <div class="row"><dt>Adresse</dt><dd>{{ companyInfo.address || '-' }}</dd></div>
            <div class="row"><dt>Ville</dt><dd>{{ [companyInfo.postcode, companyInfo.city].filter(Boolean).join(' ') || '-' }}</dd></div>
            <div class="row"><dt>Pays</dt><dd>{{ companyInfo.country || '-' }}</dd></div>
          </dl>
        </div>

        <div class="panel">
          <div class="panel-head">
            <h4>Paramétrage de la TVA</h4>
            <button type="button" class="edit-button" @click="openTvaRegimeModal">Modifier</button>
          </div>
          <dl>
            <div class="row"><dt>Régime de TVA</dt><dd>{{ vatRegimeLabel }}</dd></div>
            <div class="row"><dt>TVA sur les débits</dt><dd><span class="chip" :class="companyInfo.has_vat_on_debits ? 'chip-on' : 'chip-off'">{{ companyInfo.has_vat_on_debits ? 'Oui' : 'Non' }}</span></dd></div>
          </dl>
          <p class="panel-note">Ces réglages déterminent les mentions de TVA transmises avec vos factures électroniques.</p>
        </div>

        <div class="panel">
          <h4>Association</h4>
          <dl>
            <div class="row"><dt>Associée le</dt><dd>{{ companyInfo.created_at ? new Date(companyInfo.created_at).toLocaleDateString('fr-FR') : '-' }}</dd></div>
            <div class="row"><dt>Identifiant Noted</dt><dd>#{{ companyInfo.id }}</dd></div>
          </dl>
        </div>
      </div>
    </div>

    <div v-else class="bento-item association">
      <h3>Entreprise non associée</h3>

      <p>
        Votre entreprise n'est actuellement pas associée au service de
        facturation électronique.
      </p>

      <p>
        L'association permet à votre ERP de transmettre les factures
        électroniques de votre entreprise à la plateforme agréée (PA) chargée
        de leur acheminement dans le cadre de la réforme de la facturation
        électronique.
      </p>

      <p>
        En associant votre entreprise, vous autorisez le service à établir une
        connexion avec la plateforme agréée afin de permettre la transmission
        des factures et le suivi de leur traitement.
      </p>

      <div class="warning">
        <div class="warning-title">
          Attention
        </div>

        <div>
          L'association constitue une autorisation de connexion entre votre
          entreprise, votre ERP et la plateforme agréée. La désassociation ou
          la modification de cette autorisation peut être complexe et dépendre
          des conditions applicables auprès de la plateforme agréée.
        </div>
      </div>

      <div class="association-action">
        <button type="button" class="association-button" @click="associateCompany">
          Associer mon entreprise
        </button>
      </div>

      <p class="legal">
        En cliquant sur « Associer mon entreprise », vous serez redirigé vers
        une page externe de la plateforme agréée afin de procéder à
        l'autorisation de connexion. Les informations et conditions affichées
        sur cette page externe sont susceptibles de s'appliquer à cette
        association.
      </p>
    </div>

    <CompanyTvaRegimeModal ref="companyTvaRegimeModal" :callback="refreshCompanyInfo"/>
  </InvoiceTransmissionHeader>
</template>

<script>
import InvoiceTransmissionHeader from "@/invoice-transmission/components/InvoiceTransmissionHeader.vue";
import {CompanyController} from "@/invoice-transmission/controllers/invoice_transmission/CompanyController";
import CompanyTvaRegimeModal from "@/invoice-transmission/components/modals/CompanyTvaRegimeModal.vue";

export default {
  name: 'FactureXDashboardView',
  components: {
    CompanyTvaRegimeModal,
    InvoiceTransmissionHeader
  },
  data() {
    return {
      companyInfo: undefined,
      loading: true,
      copied: false
    };
  },
  computed: {
    // Initiales de l'entreprise pour la pastille d'en-tête
    initials() {
      const words = String(this.companyInfo?.formal_name ?? '').split(/\s+/).filter(Boolean);
      return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '?').slice(0, 2)).toUpperCase();
    },
    // Adresses électroniques autres que la principale
    otherAddresses() {
      return (this.companyInfo?.electronic_addresses ?? []).filter(address => address.identifier !== this.companyInfo.electronic_address);
    },
    vatRegimeLabel() {
      const labels = {
        monthly: 'Réel normal mensuel',
        quarterly: 'Réel normal trimestriel',
        simplified: 'Réel simplifié',
        vat_exemption: 'Franchise en base de TVA'
      };
      return labels[this.companyInfo?.vat_regime] ?? this.companyInfo?.vat_regime ?? '-';
    }
  },
  async mounted() {
    const params = new URLSearchParams(window.location.search);

    const code = params.get("code");
    const state = params.get("state");

    // Retour du callback OAuth SuperPDP
    if (code && state) {
      const callbackResp = await CompanyController.callback(code, state);

      if (callbackResp && !callbackResp.errors) {
        // Nettoyage de l'URL
        window.history.replaceState({}, document.title, window.location.pathname);

        const company = await this.fetchCompany();

        if (company) {
          this.companyInfo = company;
          localStorage.isInvoiceTransmissionLinked = true;
          window.dispatchEvent(new Event("invoice-transmission-link-changed"));
        } else {
          localStorage.isInvoiceTransmissionLinked = false;
          window.dispatchEvent(new Event("invoice-transmission-link-changed"));
        }
      }

      this.loading = false;
      return;
    }

    // Vérification si l'entreprise est déjà connectée
    const resp = await this.fetchCompany();

    if (resp) {
      this.companyInfo = resp;
      localStorage.isInvoiceTransmissionLinked = true;
      window.dispatchEvent(new Event("invoice-transmission-link-changed"));
    } else {
      localStorage.isInvoiceTransmissionLinked = false;
      window.dispatchEvent(new Event("invoice-transmission-link-changed"));
    }

    this.loading = false;
  },
  methods: {
    /**
     * Récupère l'entreprise associée, avec quelques tentatives : un échec ponctuel (ex : renouvellement du token
     * SuperPDP en cours par une autre requête) ne doit pas être pris pour une entreprise non associée.
     */
    async fetchCompany(attempts = 3) {
      for (let i = 0; i < attempts; i++) {
        const resp = await CompanyController.getSilently();
        if (resp && !resp.errors)
          return resp;

        if (i < attempts - 1)
          await new Promise(resolve => setTimeout(resolve, 400 * (i + 1)));
      }
      return undefined;
    },
    async associateCompany() {
      const authResp = await CompanyController.post();

      if (authResp && !authResp.errors) {
        // Redirection vers SuperPDP
        window.location.href = authResp.authorizationUrl;
      }
    },
    async copyAddress(address) {
      try {
        await navigator.clipboard.writeText(address);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1800);
      } catch (error) {
        this.copied = false;
      }
    },
    async openTvaRegimeModal(){
      this.$refs.companyTvaRegimeModal.openModal(this.companyInfo);
    },
    async refreshCompanyInfo(){
      const resp=await CompanyController.get();

      if(resp && !resp.errors)
        this.companyInfo=resp;
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

h1
  color THEME_COLOR_PRIMARY

  span
    color THEME_COLOR_PRIMARY_FADDED

.dashboard
  display flex
  flex-direction column
  gap 20px

// ---- En-tête : entreprise et état de la connexion
.hero
  display flex
  align-items center
  justify-content space-between
  flex-wrap wrap
  gap 14px

.hero-main
  display flex
  align-items center
  gap 16px
  min-width 0

.avatar
  display grid
  place-items center
  flex 0 0 auto
  width 52px
  height 52px
  border-radius 14px
  background PAGE_LEMON
  color THEME_COLOR_PRIMARY
  font-weight 800
  font-size 18px
  letter-spacing 0.02em

.hero-text
  min-width 0

  h2
    margin 0
    font-size 1.35em
    color THEME_COLOR_PRIMARY
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

  .trade-name
    margin 2px 0 0
    color CTRL_TEXT_MUTED
    font-size 13px

.link-status
  display inline-flex
  align-items center
  gap 8px
  padding 6px 14px
  border-radius 999px
  background #E1F3E6
  color #1f6a2c
  font-size 13px
  font-weight 700

  .dot
    width 8px
    height 8px
    border-radius 50%
    background #2e9a44
    box-shadow 0 0 0 3px rgba(46, 154, 68, 0.22)

// ---- Adresse électronique : l'information clé, mise en avant
.address-card
  padding 18px 20px
  border 1px solid #F0DE72
  border-radius 14px
  background-image linear-gradient(160deg, #FFFDEB, #FFF6BF)

.eyebrow
  display block
  margin-bottom 8px
  font-size 11.5px
  font-weight 700
  letter-spacing 0.06em
  text-transform uppercase
  color CTRL_TEXT_MUTED

.address-line
  display flex
  align-items center
  gap 12px
  flex-wrap wrap

.address
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-family ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
  font-size 1.15em
  font-weight 700
  color THEME_COLOR_PRIMARY

.copy-button, .edit-button
  padding 5px 12px
  border 1px solid rgba(THEME_COLOR_PRIMARY, 0.3)
  border-radius 8px
  background white
  color THEME_COLOR_PRIMARY
  font-weight 700
  font-size 12.5px
  cursor pointer
  transition background-color .15s ease, border-color .15s ease

  &:hover
    background-color PAGE_LEMON_HOVER
    border-color THEME_COLOR_PRIMARY

.address-help, .address-empty, .panel-note
  margin 8px 0 0
  color CTRL_TEXT_MUTED
  font-size 12.5px
  line-height 1.5

.address-others
  display flex
  flex-direction column
  gap 4px
  margin 10px 0 0
  padding 0
  list-style none

  li
    display flex
    align-items center
    gap 8px
    min-width 0

  code
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    font-size 12.5px
    color #3f3931

// ---- Panneaux d'informations
.panels
  display grid
  grid-template-columns repeat(auto-fit, minmax(260px, 1fr))
  gap 16px

.panel
  padding 16px 18px
  border 1px solid PAGE_RULE
  border-radius 14px
  background white

  h4
    margin 0 0 12px
    font-size 12px
    font-weight 800
    letter-spacing 0.06em
    text-transform uppercase
    color THEME_COLOR_PRIMARY

  dl
    margin 0
    display flex
    flex-direction column
    gap 10px

  .row
    display flex
    flex-direction column
    gap 2px

  dt
    font-size 11.5px
    font-weight 600
    color CTRL_TEXT_MUTED

  dd
    margin 0
    display flex
    align-items center
    gap 8px
    flex-wrap wrap
    color #2b2822
    font-weight 600
    overflow-wrap anywhere

.panel-head
  display flex
  align-items flex-start
  justify-content space-between
  gap 10px

.chip
  display inline-block
  padding 2px 8px
  border-radius 999px
  background #eef1f6
  color #526070
  font-size 11.5px
  font-weight 700

  &.chip-on
    background #E1F3E6
    color #1f6a2c

  &.chip-off
    background #f0f1f3
    color #626c82

// ---- Entreprise non associée
.association
  h3
    margin-bottom 15px

  p
    color #555
    line-height 1.6
    margin 0 0 15px

.warning
  display flex
  flex-direction column
  gap 8px
  margin-top 25px
  padding 15px
  border-radius 10px
  border 1px solid #e0a800
  background-color #fff3cd
  color #664d03
  line-height 1.5

.warning-title
  font-weight bold
  text-transform uppercase

.association-action
  display flex
  justify-content center
  margin-top 25px

.association-button
  padding 12px 25px
  border none
  border-radius 8px
  background-color THEME_COLOR_PRIMARY
  color THEME_COLOR_PRIMARY_OPPOSITE
  font-weight bold
  cursor pointer

  &:hover
    box-shadow 0 0 5px 0 #252525

.legal
  margin-top 20px !important
  font-size 11px
  color #888 !important
  line-height 1.5 !important
</style>
